import React from 'react';
import { supabase } from '../lib/supabaseClient';
import { debugLog } from '../lib/debugLog';
import { edgeFetch } from '../lib/edgeFetch';

const TenantContext = React.createContext({
  tenantId: '',
  tenantName: '',
  memberships: [],
  setTenantId: () => {},
  refreshMemberships: async () => {},
  siteId: '',
  setSiteId: () => {},
  siteRefreshToken: 0,
  notifySiteChange: () => {},
});

export function TenantProvider({ children }) {
  const [memberships, setMemberships] = React.useState([]);
  const [tenantId, setTenantIdState] = React.useState(() => localStorage.getItem('selected_tenant_id') || '');
  const [tenantName, setTenantName] = React.useState('');
  const [siteId, setSiteIdState] = React.useState(() => {
    const tid = localStorage.getItem('selected_tenant_id') || '';
    return tid ? (localStorage.getItem(`site_sel_${tid}`) || '') : '';
  });
  const [siteRefreshToken, setSiteRefreshToken] = React.useState(0);
  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;
  const userSelectedRef = React.useRef(false);
  const tenantIdRef = React.useRef(tenantId);
  const siteIdRef = React.useRef(siteId);
  React.useEffect(() => {
    const prev = tenantIdRef.current;
    tenantIdRef.current = tenantId;
    debugLog('[TenantContext] tenantId changed', { from: prev, to: tenantId });
  }, [tenantId]);
  React.useEffect(() => {
    const prev = siteIdRef.current;
    siteIdRef.current = siteId;
    debugLog('[TenantContext] siteId changed', { from: prev, to: siteId });
  }, [siteId]);

  const resolveName = React.useCallback((list, id) => {
    const m = (list || []).find(x => x.tenant_id === id);
    return m?.tenant_name || '';
  }, []);

  const setTenantId = React.useCallback((id) => {
    debugLog('[TenantContext] setTenantId(user)', { from: tenantIdRef.current, to: id });
    userSelectedRef.current = true;
    setTenantIdState(id || '');
    if (id) localStorage.setItem('selected_tenant_id', id); else localStorage.removeItem('selected_tenant_id');
    // Clear siteId and its localStorage key on tenant switch.
    // SiteSwitcherInline will resolve the correct siteId from the server and persist it.
    // Without this, a stale site_sel_ value (from localStorage contamination or a prior session)
    // can reference a site belonging to a different tenant, causing cross-tenant data overwrites.
    setSiteIdState('');
    if (id) { try { localStorage.removeItem(`site_sel_${id}`); } catch (_) {} }
  }, []);
  const setSiteId = React.useCallback((sid) => {
    const tid = tenantIdRef.current || '';
    setSiteIdState(sid || '');
    if (tid) {
      if (sid) localStorage.setItem(`site_sel_${tid}`, sid);
      else localStorage.removeItem(`site_sel_${tid}`);
    }
  }, []);
  const notifySiteChange = React.useCallback(() => {
    setSiteRefreshToken((prev) => prev + 1);
  }, []);

  const refreshMemberships = React.useCallback(async () => {
    debugLog('[TenantContext] refreshMemberships: start');
    try {
      let tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      for (let i = 0; !tok && i < 20; i++) { await new Promise(r=>setTimeout(r,150)); tok = (await supabase.auth.getSession()).data.session?.access_token || ''; }
      if (!tok) { setMemberships([]); return; }
      try {
        await edgeFetch(`${edgeBase}/functions/v1/invites-auto-accept`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: '{}' });
      } catch (_) { /* ignore */ }
      const res = await edgeFetch(`${edgeBase}/functions/v1/user-memberships`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: '{}' });
      if (!res.ok) { setMemberships([]); return; }
      const j = await res.json();
      const list = Array.isArray(j.memberships) ? j.memberships : [];
      setMemberships(list);
      // Initialize or repair selection without clobbering user choice
      const ids = new Set(list.map(m => m.tenant_id));
      const stored = localStorage.getItem('selected_tenant_id') || '';
      const current = tenantIdRef.current || stored;
      debugLog('[TenantContext] refreshMemberships: state', { current, stored, ids: Array.from(ids), userSelected: userSelectedRef.current });
      if (!current) {
        debugLog('[TenantContext] selecting first membership (no current)');
        const first = list[0]?.tenant_id || '';
        if (first) setTenantId(first);
      } else if (!ids.has(current) && !userSelectedRef.current) {
        debugLog('[TenantContext] repairing selection (current not in memberships and no user selection)');
        const first = list[0]?.tenant_id || '';
        if (first) setTenantId(first);
      }
      const resolvedName = resolveName(list, tenantIdRef.current || stored) || resolveName(list, list[0]?.tenant_id);
      setTenantName(resolvedName);
      debugLog('[TenantContext] setTenantName', { tenantId: tenantIdRef.current, tenantName: resolvedName });
    } catch (_) { setMemberships([]); }
  }, [edgeBase, resolveName, setTenantId]);

  React.useEffect(() => { refreshMemberships(); }, [refreshMemberships]);
  React.useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      debugLog('[TenantContext] auth state change', { hasUser: !!session?.user });
      if (session?.user) {
        refreshMemberships();
      }
    });
    return () => {
      try { sub.subscription?.unsubscribe?.(); } catch (_) {}
      try { sub.unsubscribe?.(); } catch (_) {}
    };
  }, [refreshMemberships]);
  React.useEffect(() => {
    const name = resolveName(memberships, tenantId);
    setTenantName(name);
    debugLog('[TenantContext] tenantName effect', { tenantId, name });
  }, [memberships, tenantId, resolveName]);

  const value = React.useMemo(
    () => ({
      tenantId,
      tenantName,
      memberships,
      setTenantId,
      refreshMemberships,
      siteId,
      setSiteId,
      siteRefreshToken,
      notifySiteChange,
    }),
    [tenantId, tenantName, memberships, setTenantId, refreshMemberships, siteId, setSiteId, siteRefreshToken, notifySiteChange],
  );
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return React.useContext(TenantContext);
}


